import { Callout } from "@/components/ui/callout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "@/lib/i18n/client";
import { useLocation } from "@tanstack/react-router";
import { useConfigStore } from "@/store/use-config-store";
import { useIsMobile } from "@/utils/useIsMobile";
import useUserAgent from "@/utils/useUserAgent";
import { isPWA } from "@/utils/util";
import { PlusSquare } from "lucide-react";
import { useEffect, useState } from "react";
import * as Swetrix from "swetrix";
import { useShallow } from "zustand/react/shallow";
import { Button } from "../../../../components/ui/button";
import Spinner from "../spinner";
import useIosInstallPrompt from "./hooks/useIosInstallPrompt";
import useWebInstallPrompt from "./hooks/useWebInstallPrompt";
import ShareIcon from "./icon/shareicon";
import { useLng } from "@/hooks/use-lng";

export default function PromptInstall() {
  const [iosInstallPrompt, handleIOSInstallDeclined] = useIosInstallPrompt();
  const [webInstallPrompt, handleWebInstallDeclined, handleWebInstallAccepted] =
    useWebInstallPrompt();
  const lng = useLng();
  const { t } = useTranslation(lng, "translation");
  const [settings] = useConfigStore(useShallow((state) => [state.settings]));
  const isMobile = useIsMobile();
  const [show, setShow] = useState(false);
  const pathname = useLocation({ select: (l) => l.pathname });
  const shouldUpdate = !isPWA() && pathname.includes("update");
  const { userAgent, userAgentString, isIOS, isStandalone } = useUserAgent();
  void userAgentString;
  void isIOS;
  void isStandalone;
  const isSamsungBrowser = userAgent === "SamsungBrowser";

  useEffect(() => {
    setTimeout(() => {
      setShow(true);
    }, 60000);
  }, []);

  useEffect(() => {
    if (shouldUpdate) {
      setShow(true);
    }
    if (window.matchMedia("(display-mode: standalone)").matches) {
      Swetrix.track({
        ev: "using_pwa",
        unique: true,
        meta: {
          tenant: settings?.shopName
        }
      });
    }
  }, [pathname, settings?.shopName, shouldUpdate]);

  if (!isMobile || (!iosInstallPrompt && !webInstallPrompt)) {
    return null;
  }

  return (
    <Card
      className={`${
        show ? "visible block" : "invisible hidden"
      } animate-in fade-in-0 zoom-in-95 fixed top-0 right-0 left-0 z-999 mx-auto rounded-none md:bottom-6 md:w-[500px] md:rounded-xl`}
    >
      <CardHeader className="pb-1">
        <CardTitle className="flex items-center justify-between text-lg">
          {t("install-app-our")}
          {settings?.selfUrl && (
            <img
              className="h-10 w-10 rounded-lg"
              alt="logo"
              src={`${settings?.selfUrl}/ee-static/membersapp/icons/icon-192x192.png`}
            />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex justify-end px-4">
        {!shouldUpdate && iosInstallPrompt && (
          <div className="flex flex-col gap-4">
            <div className="inline">
              {t("install-app-description-ios")}
              <span className="bg-background mx-1 inline-flex items-center rounded-sm">
                <ShareIcon modern={true} />
              </span>
              , {t("install-app-confirmation-safari-continue-1")}
              <div className="text-foreground mt-4 rounded-md border p-2">
                <span className="flex w-full items-center justify-between px-2">
                  {t("install-app-confirmation-safari-continue-2")}{" "}
                  <PlusSquare className="inline rounded-lg" />
                </span>
              </div>
            </div>

            <Button className="mt-3" variant="default" onClick={handleIOSInstallDeclined}>
              {t("install-app-dont-show-again")}
            </Button>
          </div>
        )}
        {!shouldUpdate && webInstallPrompt && (
          <div className="flex flex-col">
            <div className="w-full text-left">
              <p>{t("install-app-description")}</p>
            </div>
            {isSamsungBrowser && (
              <Callout
                title={t("install-app-samsung-warning-title")}
                variant="warning"
                className="mt-4"
              >
                <span>{t("install-app-samsung-warning-description")}</span>
              </Callout>
            )}
            <div className="mt-4 flex flex-col justify-end gap-4">
              <Button className="min-w-32" variant="secondary" onClick={handleWebInstallDeclined}>
                <div>{t("install-app-dont-show-again")}</div>
              </Button>
              <Button className="w-full" variant="default" onClick={handleWebInstallAccepted}>
                {t("install-app-install")}
              </Button>
            </div>
          </div>
        )}
        {shouldUpdate && (iosInstallPrompt || webInstallPrompt) ? (
          <div className="flex flex-col">
            {iosInstallPrompt ? (
              <div className="inline">
                {t("install-app-description-ios-update")}
                <span className="bg-background mx-1 inline-flex items-center rounded-sm">
                  <ShareIcon modern={true} />
                </span>
                , {t("install-app-confirmation-safari-continue-1")}
                <div className="text-foreground mt-4 rounded-md border p-2">
                  <span className="flex w-full items-center justify-between px-2">
                    {t("install-app-confirmation-safari-continue-2")}{" "}
                    <PlusSquare className="inline rounded-lg" />
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="w-full text-left">
                  <p>{t("install-app-description-update")}</p>
                </div>
                {isSamsungBrowser && (
                  <Callout
                    title={t("install-app-samsung-warning-title")}
                    variant="warning"
                    className="mt-4"
                  >
                    <span>{t("install-app-samsung-warning-description")}</span>
                  </Callout>
                )}
                <div className="mt-4 flex justify-end gap-4">
                  <Button className="w-full" variant="default" onClick={handleWebInstallAccepted}>
                    {t("install-app-install")}
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          shouldUpdate && (
            <div className="mt-12 h-full w-full">
              <Spinner />
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
